import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  ThreadListResponseDto,
  ThreadListQuery,
  MessagesResponseDto,
  MessagesQuery,
  ContactDetailsDto,
} from '../types';

const QUERY_KEY_THREADS = 'conversations-threads';
const QUERY_KEY_MESSAGES = 'conversations-messages';
const QUERY_KEY_CONTACT = 'conversations-contact';

/**
 * Hook for fetching threads with infinite scroll
 */
export function useThreads(filters?: ThreadListQuery) {
  return useInfiniteQuery<ThreadListResponseDto>({
    queryKey: [QUERY_KEY_THREADS, filters],
    queryFn: async ({ pageParam = 0 }) => {
      const params = {
        ...filters,
        offset: pageParam as number,
        limit: filters?.limit ?? 20,
      };

      const { data } = await api.get<ThreadListResponseDto>(
        '/conversations/threads',
        { params },
      );

      return data;
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.hasMore) {
        return lastPage.offset + lastPage.limit;
      }
      return undefined;
    },
  });
}

/**
 * Hook for fetching messages from a thread with infinite scroll
 */
export function useMessages(
  threadId: string | undefined,
  options?: MessagesQuery,
) {
  return useInfiniteQuery<MessagesResponseDto>({
    queryKey: [QUERY_KEY_MESSAGES, threadId, options],
    queryFn: async ({ pageParam = 0 }) => {
      const params = {
        ...options,
        offset: pageParam as number,
        limit: options?.limit ?? 50,
      };

      const { data } = await api.get<MessagesResponseDto>(
        `/conversations/threads/${threadId}/messages`,
        { params },
      );

      return data;
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.hasMore) {
        return (lastPage.items.length || 0);
      }
      return undefined;
    },
    enabled: !!threadId, // Only run if threadId is truthy
  });
}

/**
 * Hook for fetching contact details with SystemUnits
 */
export function useContactDetails(senderId: string | undefined) {
  return useQuery<ContactDetailsDto>({
    queryKey: [QUERY_KEY_CONTACT, senderId],
    queryFn: async () => {
      const { data } = await api.get<ContactDetailsDto>(
        `/conversations/contacts/${senderId}`,
      );
      return data;
    },
    enabled: !!senderId, // Only run if senderId is truthy
    staleTime: 1000 * 60 * 5, // 5 minutes (since data is cached on backend)
  });
}
